import { Component, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { filter } from 'rxjs';

import { PAGE_INFO, PageInfo } from './page-info.registry';

const VISIBLE_KEY = 'ahla.showPageInfo';

function readVisible(): boolean {
  try {
    return localStorage.getItem(VISIBLE_KEY) !== '0';
  } catch {
    return true;
  }
}

// نمایش یا عدم نمایش آیکن «راهنما» در صفحات (تنظیم هر دستگاه؛ از «تنظیمات نمایش» در نوار بالا)
export const pageInfoVisible = signal(readVisible());

export function setPageInfoVisible(visible: boolean) {
  pageInfoVisible.set(visible);
  try {
    localStorage.setItem(VISIBLE_KEY, visible ? '1' : '0');
  } catch {
    // فقط برای همین نشست
  }
}

// آیکن «راهنما»: با زدن روی آن، خلاصه‌ی کارهایی که سامانه در این بخش انجام می‌دهد باز می‌شود
@Component({
  selector: 'app-page-info',
  standalone: true,
  imports: [CommonModule, PopoverModule, TooltipModule],
  template: `
    @if (pageInfoVisible() && current(); as info) {
      <div class="page-info-bar" [class.inline]="inline">
        <button
          type="button"
          class="page-info-btn"
          [class.on-dark]="onDark"
          (click)="panel.toggle($event)"
          [attr.aria-label]="'راهنمای ' + info.title"
          pTooltip="راهنمای این بخش"
          tooltipPosition="bottom"
        >
          <i class="pi pi-info-circle"></i>
        </button>
      </div>
      <p-popover #panel [style]="{ maxWidth: 'min(26rem, 92vw)' }">
        <div class="page-info-pop">
          <div class="page-info-title">
            <i [class]="info.icon"></i>
            {{ info.title }}
          </div>
          @if (info.ordered) {
            <ol class="page-info-list">
              @for (p of info.points; track p) {
                <li>{{ p }}</li>
              }
            </ol>
          } @else {
            <ul class="page-info-list">
              @for (p of info.points; track p) {
                <li>{{ p }}</li>
              }
            </ul>
          }
          @if (info.footer) {
            <p class="page-info-footer">{{ info.footer }}</p>
          }
        </div>
      </p-popover>
    }
  `,
  styles: [
    `
      .page-info-bar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 0.5rem;
      }
      .page-info-bar.inline {
        display: inline-flex;
        margin: 0;
      }
      .page-info-btn {
        width: 2.2rem;
        height: 2.2rem;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--surface-border);
        background: var(--surface-card);
        color: var(--primary-color);
        cursor: pointer;
        font-size: 1.1rem;
        transition: background-color 0.15s;
      }
      .page-info-btn:hover {
        background: color-mix(in srgb, var(--primary-color) 12%, var(--surface-card));
      }
      .page-info-btn.on-dark {
        background: rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.3);
        color: #fff;
      }
      .page-info-pop {
        direction: rtl;
        text-align: right;
      }
      .page-info-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: 700;
        margin-bottom: 0.6rem;
      }
      .page-info-title i {
        color: var(--primary-color);
      }
      .page-info-footer {
        margin: 0.6rem 0 0;
        font-size: 0.88rem;
        line-height: 1.8;
        font-weight: 600;
      }
      .page-info-list {
        margin: 0;
        padding-inline-start: 1.1rem;
        display: flex;
        flex-direction: column;
        gap: 0.45rem;
        font-size: 0.88rem;
        line-height: 1.8;
        color: var(--text-color-secondary);
      }
    `,
  ],
})
export class PageInfoComponent {
  // بدون مقدار: راهنمای صفحه‌ی فعلی پنل (از روی مسیر)
  @Input() info: PageInfo | null = null;
  @Input() onDark = false;
  // نمایش آیکن در کنار عناصر دیگر به‌جای یک سطر جداگانه
  @Input() inline = false;

  readonly pageInfoVisible = pageInfoVisible;
  private router = inject(Router);
  private routeInfo = signal<PageInfo | null>(null);

  constructor() {
    this.update(this.router.url);
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e) => this.update((e as NavigationEnd).urlAfterRedirects));
  }

  current() {
    return this.info ?? this.routeInfo();
  }

  private update(url: string) {
    const path = url.split(/[?#]/)[0].replace(/^\//, '');
    this.routeInfo.set(PAGE_INFO[path] ?? null);
  }
}
